import * as React from 'react';
import { Box, Modal, Theme, useMediaQuery, styled } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DateRange, rangeToLabel } from 'src/utils/date';
import DatePicker from '../DatePicker';
import Column from 'src/frontend-common/components/Column';
import Card from 'src/frontend-common/components/Card';
import ButtonComponent from 'src/components/ButtonComponent';

interface Props {
  dateRange: DateRange;
  setDateRange: (r: DateRange) => void;
  disabled?: boolean;
}

interface Position {
  top: number | string;
  right: number | string;
}

interface ButtonProps {
  setPosition: (p: Position) => void;
  toggle: () => void;
  label: string;
  disabled?: boolean;
}

const CardWrapper = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const DateButton = React.memo<ButtonProps>(
  ({ toggle, label }) => (
    <ButtonComponent
      buttontype="calendar"
      iconcolor="textPrimary"
      text={label}
      icon={<CalendarTodayIcon />}
      onClick={toggle}
      style={{
        borderRadius: 4,
      }}
    />
  ),
  (prev, next) => prev.label === next.label && prev.disabled === next.disabled
);

DateButton.displayName = 'DateButton';

const DateFilter: React.FC<Props> = ({ dateRange, setDateRange, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const [selectedRange, setSelectedRange] = React.useState<DateRange>(dateRange);
  const isDesktopUp = useMediaQuery<Theme>((t) => t.breakpoints.up('md'));
  const [position, setPosition] = React.useState<Position>({
    top: 0,
    right: 0,
  });
  const toggle = () => setOpen(!open);
  const selectDate = () => {
    toggle();
    setDateRange(selectedRange);
  };

  return (
    <Box
      data-testid="date-filter"
      style={{
        display: 'flex',
        height: '100%',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      <DateButton
        disabled={disabled}
        data-testid="date-filter-button"
        setPosition={(p) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isDesktopUp ? setPosition(p) : setPosition({ top: p.top, right: '5%' });
        }}
        toggle={toggle}
        label={rangeToLabel(dateRange)}
      />
      <Modal
        open={open}
        onClose={() => {
          toggle();
        }}
      >
        <div
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              selectDate();
            }
          }}
          style={{
            maxWidth: '90%',
            zIndex: -1,
            position: 'absolute',
            ...position,
          }}
        >
          <CardWrapper>
            <Column hAlign="flex-end">
              <DatePicker
                disabled={disabled}
                open={open}
                months={isDesktopUp ? 2 : 1}
                selectedRange={selectedRange}
                onChange={(range) => {
                  setSelectedRange(range);
                }}
              />
              <ButtonComponent buttontype="mainaction" text="Select date" onClick={selectDate} />
            </Column>
          </CardWrapper>
        </div>
      </Modal>
    </Box>
  );
};

export default DateFilter;
